import { useCallback, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { DragEndEvent } from "@dnd-kit/core";
import { IUseFunctionalLocationsReorderArgs } from "./interfaces";

export const useFunctionalLocationsReorder = ({
    areas,
    setAreas,
    refetch,
    setFunctionalLocationsOrder,
}: IUseFunctionalLocationsReorderArgs) => {
    const [isDraggingFunctionalLocations, setIsDraggingFunctionalLocations] = useState(false);

    const onDragMove = useCallback(() => {
        setIsDraggingFunctionalLocations(true);
    }, []);

    const onDragEnd = useCallback(
        async (event: DragEndEvent) => {
            const { active, over } = event;
            setIsDraggingFunctionalLocations(false);
            if (active.id !== over?.id && areas && setAreas) {
                // Find the area that contains the functional location being dragged
                const draggedFunctionalLocationId = parseInt(active.id.toString());
                const targetFunctionalLocationId = parseInt(over?.id?.toString() || "0");

                const sourceArea = areas.find((area) =>
                    area.functional_locations.some((fl) => fl.functional_location_id === draggedFunctionalLocationId),
                );

                const targetArea = areas.find((area) =>
                    area.functional_locations.some((fl) => fl.functional_location_id === targetFunctionalLocationId),
                );

                if (sourceArea && targetArea && sourceArea.area_id === targetArea.area_id) {
                    // Only allow reordering within the same area
                    // Functional locations are already sorted by order from the GraphQL query
                    const functionalLocations = sourceArea.functional_locations;

                    const oldIndex = functionalLocations.findIndex(
                        (fl) => fl.functional_location_id === draggedFunctionalLocationId,
                    );
                    const newIndex = functionalLocations.findIndex(
                        (fl) => fl.functional_location_id === targetFunctionalLocationId,
                    );

                    if (oldIndex !== -1 && newIndex !== -1) {
                        const newFunctionalLocations = arrayMove(functionalLocations, oldIndex, newIndex);

                        // Update local state immediately for visual feedback - same approach as areas
                        const updatedAreas = areas.map((area) => {
                            if (area.area_id === sourceArea.area_id) {
                                return {
                                    ...area,
                                    functional_locations: newFunctionalLocations,
                                };
                            }
                            return area;
                        });
                        setAreas(updatedAreas);

                        try {
                            // Update with null order to avoid conflict
                            await setFunctionalLocationsOrder({
                                variables: {
                                    functional_locations: newFunctionalLocations.map((fl) => ({
                                        functional_location_id: fl.functional_location_id,
                                        order: null,
                                        area_id: fl.area_id,
                                        name_translation_id: fl.translation.id,
                                    })),
                                },
                            });

                            // Update with new ordering
                            await setFunctionalLocationsOrder({
                                variables: {
                                    functional_locations: newFunctionalLocations.map((fl, index) => ({
                                        functional_location_id: fl.functional_location_id,
                                        order: index + 1,
                                        area_id: fl.area_id,
                                        name_translation_id: fl.translation.id,
                                    })),
                                },
                            });

                            await refetch();
                        } catch (error) {
                            console.error("Failed to update functional locations order", error);
                            // Revert local state on error
                            setAreas(areas);
                        }
                    }
                }
            }
        },
        [areas, refetch, setFunctionalLocationsOrder, setAreas],
    );

    return { isDraggingFunctionalLocations, onDragMove, onDragEnd };
};
